import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import PasswordInput from '../../components/UI/PasswordInput';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showResendOption, setShowResendOption] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResendConfirmation = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address first');
      return;
    }

    setResendLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.RESEND_CONFIRMATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Confirmation email sent! Check your inbox.');
        setShowResendOption(false);
      } else {
        toast.error(data.message || 'Failed to resend confirmation email');
      }
    } catch (error) {
      console.error('Resend confirmation error:', error);
      toast.error('Failed to resend confirmation email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');

      // Smart redirect based on app type
      const isDashApp = window.location.pathname.startsWith('/dash/');
      const urlParams = new URLSearchParams(window.location.search);
      const redirectTo = urlParams.get('redirect');

      if (redirectTo) {
        navigate(redirectTo);
      } else if (isDashApp) {
        // Dash app - focus on errand services
        navigate('/errands');
      } else {
        // Main app - default to home
        navigate('/');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);

      // Check if it's an email confirmation error - be more comprehensive
      const lowerErrorMessage = errorMessage.toLowerCase();
      const isEmailConfirmationError =
        lowerErrorMessage.includes('email not confirmed') ||
        lowerErrorMessage.includes('confirmation link') ||
        lowerErrorMessage.includes('invalid login credentials') ||
        (lowerErrorMessage.includes('email') && (
          lowerErrorMessage.includes('confirm') ||
          lowerErrorMessage.includes('verify') ||
          lowerErrorMessage.includes('unconfirmed') ||
          lowerErrorMessage.includes('not confirmed') ||
          lowerErrorMessage.includes('confirmation')
        ));

      if (isEmailConfirmationError) {
        setShowResendOption(true);
        // Stay on email login and show resend option - don't redirect to phone auth
        // This provides a clearer user flow for email users
      } else {
        setShowResendOption(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <input
                type="email"
                name="email"
                required
                className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Email address"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Password"
                required
                error={errors.password}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/auth/signup" className="text-blue-600 hover:text-blue-500 font-medium">
                Sign up
              </Link>
            </p>
          </div>



          <div className="text-center">
            <p className="text-sm text-gray-600">
              Forgot your password?{' '}
              <Link to="/auth/forgot-password" className="text-blue-600 hover:text-blue-500 font-medium">
                Reset it
              </Link>
            </p>
          </div>

          {showResendOption && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-center">
                <p className="text-sm text-yellow-800 mb-3">
                  Your email address hasn't been confirmed yet. Please check your email for the confirmation link.
                </p>
                <div className="flex flex-col space-y-2">
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resendLoading}
                    className="text-sm bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Confirmation Email'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
